import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OverlayDialog from "../OverlayDialog.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { fetchInventoryPage, INVENTORY_PAGE_SIZE } from "../endpoints/inventoryTable.jsx";
import { listDirectoryUsers } from "../../lib/identityClient.js";
import PaginatedPicker, { PickerRow } from "./PaginatedPicker.jsx";
import { ProviderTag } from "./EscalationParts.jsx";
import {
    MAX_JUSTIFICATION_CHARS,
    WINDOW_PRESETS,
    canEscalateOnSubject,
    createEscalationRequest,
    subjectKey,
    subjectLabel,
} from "../../lib/escalationClient.js";
import { describeTargetShape, formatWindow } from "../../lib/escalationLabels.js";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

const PAGE_SIZE = 50;
const DEFAULT_WINDOW_SECONDS = 14400;
// The subject feed is search-only server-side (MIN_SUBJECT_SEARCH_CHARS), so an
// empty search is rejected rather than answered with a first page.
const MIN_PERSON_SEARCH_CHARS = 3;

const INPUT_CLASS = "min-h-[32px] w-full border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]";
const SECTION_LABEL_CLASS = "text-[10px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]";

function Choice({ active, onClick, children, disabled = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            className={[
                "border px-[10px] py-[5px] text-[11px] leading-[1.1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer",
                active
                    ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                    : "border-[color:var(--line)] bg-transparent text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

function MachinePicker({ selected, onSelect }) {
    const fetchPage = useCallback(async ({ search, cursor }) => {
        const pageNumber = cursor ?? 0;
        const page = await fetchInventoryPage(pageNumber, search);
        const seen = pageNumber * INVENTORY_PAGE_SIZE + page.rows.length;
        return {
            rows: page.rows,
            total: page.total,
            nextCursor: page.total != null && seen < page.total && page.rows.length
                ? pageNumber + 1
                : null,
        };
    }, []);

    return (
        <PaginatedPicker
            placeholder="Search by hostname, user, or serial"
            ariaLabel="Search endpoints"
            emptyText="No endpoints match that search."
            errorText={(err) => err?.serverMessage || "Could not search endpoints."}
            fetchPage={fetchPage}
            renderRow={(row) => (
                <PickerRow
                    key={row.idRaw}
                    primary={row.hostname || row.id}
                    secondary={row.registeredUser || row.platform}
                    selected={selected?.id === row.idRaw}
                    onSelect={() => onSelect({ id: row.idRaw, label: row.hostname || row.id })}
                />
            )}
        />
    );
}

function PersonPicker({ selected, onSelect, initialSearch = "" }) {
    const fetchPage = useCallback(async ({ search, cursor }) => {
        const page = await listDirectoryUsers({
            search,
            page_size: PAGE_SIZE,
            page_token: cursor ?? "",
        });
        return {
            rows: page?.users ?? [],
            total: page?.totalCount ?? null,
            nextCursor: page?.nextPageToken || null,
        };
    }, []);

    return (
        <PaginatedPicker
            initialSearch={initialSearch}
            placeholder="Search people by name, email, or department"
            ariaLabel="Search people"
            emptyText="No people match that search."
            errorText={(err) => err?.serverMessage || "Could not search people."}
            fetchPage={fetchPage}
            renderRow={(row) => {
                const subject = {
                    sourceProvider: row.sourceProvider ?? "",
                    integrationId: row.integrationId ?? "",
                    sourceId: row.sourceId ?? "",
                    displayName: row.displayName || row.email || row.sourceId || "",
                };
                const key = subjectKey(subject);
                return (
                    <PickerRow
                        key={key}
                        primary={subjectLabel(subject)}
                        secondary={row.displayName ? (row.email || row.department) : row.department}
                        badge={<ProviderTag provider={subject.sourceProvider} />}
                        selected={selected ? subjectKey(selected) === key : false}
                        onSelect={() => onSelect(subject)}
                    />
                );
            }}
        />
    );
}

// Which halves of the target each mode requires. `both` requires both, which is
// the whole difference between it and asking for the person or the machine.
function targetReadyFor(mode, target) {
    if (mode === "person") return Boolean(target.subject);
    if (mode === "machine") return Boolean(target.endpointId);
    return Boolean(target.subject && target.endpointId);
}

const TARGET_INCOMPLETE = Object.freeze({
    person: "Choose the person to request access to.",
    machine: "Choose the machine to request access to.",
    both: "Choose both a person and a machine. Asking for one of them requests wider access "
        + "than this option grants.",
});

export default function EscalationRequestDialog({ open, onClose, onSubmitted, seed = null }) {
    const caps = useMyPermissions();

    const subjectsEscalatable = canEscalateOnSubject(caps);

    const [subject, setSubject] = useState(null);
    const [endpoint, setEndpoint] = useState(null);
    const [mode, setMode] = useState(subjectsEscalatable ? "person" : "machine");
    const [editingTarget, setEditingTarget] = useState(true);
    const [justification, setJustification] = useState("");
    const [seconds, setSeconds] = useState(DEFAULT_WINDOW_SECONDS);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const seedSubjectKey = subjectKey(seed?.subject);
    const seedEndpointId = seed?.endpoint?.id ?? "";
    const seedPersonSourceId = seed?.person?.sourceId ?? "";
    const seedPersonSearch = seed?.person?.search ?? "";

    // Read through a ref so the reset keys on what the seed IDENTIFIES, not on
    // the object: callers rebuild it every render, and depending on it wiped a
    // resolved selection the moment anything above re-rendered.
    const seedRef = useRef(seed);
    seedRef.current = seed;

    // A dismissed draft must not leak into the next open, which would file one
    // justification against a different target.
    useEffect(() => {
        if (!open) return;
        setSubject(subjectsEscalatable ? (seedRef.current?.subject ?? null) : null);
        setEndpoint(seedRef.current?.endpoint ?? null);
        setEditingTarget(!seedEndpointId && !(subjectsEscalatable && (seedSubjectKey || seedPersonSourceId)));
        if (!subjectsEscalatable) {
            setMode("machine");
        } else {
            setMode(seedSubjectKey && seedEndpointId
                ? "both"
                : seedEndpointId ? "machine" : "person");
        }
        setJustification("");
        setSeconds(DEFAULT_WINDOW_SECONDS);
        setError(null);
        setBusy(false);
        setMounted({ person: subjectsEscalatable, machine: !subjectsEscalatable || Boolean(seedEndpointId) });
    }, [open, seedSubjectKey, seedEndpointId, seedPersonSourceId, subjectsEscalatable]);

    // Opened from a person in the explorer, which names them by provider id
    // only. Resolve that against the directory and select them outright; the
    // search term is just what brings the row into the page, so the match is on
    // the id. A miss leaves the picker open on that search.
    const [resolvingPerson, setResolvingPerson] = useState(false);
    useEffect(() => {
        // No search term means the directory cannot find them: it matches on
        // display name, email and UPN, never on the source id.
        if (!open || !subjectsEscalatable) return undefined;
        if (!seedPersonSourceId || seedSubjectKey || !seedPersonSearch) return undefined;
        let cancelled = false;
        setResolvingPerson(true);
        listDirectoryUsers({ search: seedPersonSearch, page_size: PAGE_SIZE })
            .then((page) => {
                if (cancelled) return;
                const row = (page?.users ?? []).find((user) => user.sourceId === seedPersonSourceId);
                if (!row) return;
                setSubject({
                    sourceProvider: row.sourceProvider ?? "",
                    integrationId: row.integrationId ?? "",
                    sourceId: row.sourceId ?? "",
                    displayName: row.displayName || row.email || row.sourceId || "",
                });
                setEditingTarget(false);
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setResolvingPerson(false); });
        return () => { cancelled = true; };
    }, [open, seedPersonSourceId, seedPersonSearch, seedSubjectKey, subjectsEscalatable]);

    const [mounted, setMounted] = useState({
        person: subjectsEscalatable,
        machine: !subjectsEscalatable,
    });
    useEffect(() => {
        setMounted((current) => ({
            person: current.person || mode !== "machine",
            machine: current.machine || mode !== "person",
        }));
    }, [mode]);

    const target = useMemo(() => ({
        subject: mode === "machine" ? null : subject,
        endpointId: mode === "person" ? "" : (endpoint?.id ?? ""),
    }), [mode, subject, endpoint]);

    const shape = describeTargetShape(target);
    // Per mode, not "either half will do": submitting "one person, one machine"
    // with only the person asks for that person on every machine, and with only
    // the machine asks for everyone on it. Both are wider than the choice made.
    const targetReady = targetReadyFor(mode, target);
    const justificationReady = justification.trim().length > 0
        && justification.length <= MAX_JUSTIFICATION_CHARS;
    const submit = useCallback(async (event) => {
        event.preventDefault();
        // Checked again here, not only on the button: the form submits on Enter
        // too, and a disabled button is a hint rather than a gate.
        if (!targetReadyFor(mode, target)) {
            setError(TARGET_INCOMPLETE[mode] ?? TARGET_INCOMPLETE.both);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const created = await createEscalationRequest({
                subject: target.subject,
                endpointId: target.endpointId,
                justification: justification.trim(),
                requestedSeconds: seconds,
            });
            onSubmitted?.(created);
            onClose?.();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setBusy(false);
        }
    }, [mode, target, justification, seconds, onSubmitted, onClose, caps]);

    const targetSummary = [
        subject && mode !== "machine" ? subjectLabel(subject) : null,
        endpoint && mode !== "person" ? endpoint.label : null,
    ].filter(Boolean).join(" on ");

    return (
        <OverlayDialog
            open={open}
            onClose={onClose}
            analyticsId={DIALOG.ESCALATION_REQUEST}
            eyebrow="Escalation"
            title="Request temporary access"
            width="600px"
        >
            <form onSubmit={submit} className="grid gap-[16px]">
                <p className="m-0 text-[12px] leading-[1.5] text-[color:var(--muted)]">
                    Request access to prompt and response text, raw events, and device details for
                    the selected target.
                    {subjectsEscalatable ? null : " Only machine targets are available in this organization."}
                </p>

                <section className="grid gap-[8px]">
                    <div className="flex items-baseline justify-between gap-[10px]">
                        <span className={SECTION_LABEL_CLASS}>Target</span>
                        {!editingTarget && targetSummary ? (
                            <button
                                type="button"
                                onClick={() => setEditingTarget(true)}
                                className="text-[10px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                            >
                                Change
                            </button>
                        ) : null}
                    </div>
                    {resolvingPerson && !targetSummary ? (
                        <p
                            aria-busy="true"
                            className={`${NOTE_CLASS} m-0 border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[9px] text-[11px]`}
                        >
                            Looking up {seed?.person?.label || "this person"}…
                        </p>
                    ) : !editingTarget && targetSummary ? (
                        <div className="grid gap-[3px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]">
                            <span className="flex min-w-0 items-center gap-[6px]">
                                <span className="truncate text-[13px] leading-[1.2] font-medium text-[color:var(--ink)]">
                                    {targetSummary}
                                </span>
                                {subject && mode !== "machine"
                                    ? <ProviderTag provider={subject.sourceProvider} />
                                    : null}
                            </span>
                            <span className={`${NOTE_CLASS} text-[11px]`}>{shape.sentence}.</span>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-[6px]">
                                {subjectsEscalatable ? (
                                    <>
                                        <Choice active={mode === "person"} onClick={() => setMode("person")}>One person</Choice>
                                        <Choice active={mode === "both"} onClick={() => setMode("both")}>One person, one machine</Choice>
                                    </>
                                ) : null}
                                <Choice active={mode === "machine"} onClick={() => setMode("machine")}>One machine</Choice>
                            </div>
                            {/* Kept mounted once shown so a search survives a
                                mode toggle, and so neither picker silently
                                refetches while the other does not. */}
                            {mounted.person && subjectsEscalatable ? (
                                <div className={mode === "machine" ? "hidden" : undefined}>
                                    <PersonPicker
                                        selected={subject}
                                        onSelect={setSubject}
                                        initialSearch={seedPersonSearch}
                                    />
                                </div>
                            ) : null}
                            {mounted.machine ? (
                                <div className={mode === "person" ? "hidden" : undefined}>
                                    <MachinePicker selected={endpoint} onSelect={setEndpoint} />
                                </div>
                            ) : null}
                            <span className={`${NOTE_CLASS} text-[11px] leading-[1.4]`}>
                                {targetReady ? `${shape.sentence}.` : TARGET_INCOMPLETE[mode]}
                            </span>
                        </>
                    )}
                </section>

                <section className="grid gap-[6px]">
                    <div className="flex items-baseline justify-between gap-[10px]">
                        <span className={SECTION_LABEL_CLASS}>Reason</span>
                        <span className={`${NOTE_CLASS} tabular-nums ${justification.length > MAX_JUSTIFICATION_CHARS ? "text-red-400" : ""}`}>
                            {justification.length} / {MAX_JUSTIFICATION_CHARS}
                        </span>
                    </div>
                    <textarea
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        rows={3}
                        aria-label="Why you need this access"
                        placeholder="Describe the investigation and why you need access."
                        className={`${INPUT_CLASS} resize-y py-[7px] leading-[1.42]`}
                    />
                </section>

                <section className="grid gap-[6px]">
                    <span className={SECTION_LABEL_CLASS}>Duration</span>
                    <div className="flex flex-wrap gap-[6px]">
                        {WINDOW_PRESETS.map((preset) => (
                            <Choice
                                key={preset.seconds}
                                active={seconds === preset.seconds}
                                onClick={() => setSeconds(preset.seconds)}
                            >
                                {preset.label}
                            </Choice>
                        ))}
                    </div>
                    <span className={`${NOTE_CLASS} text-[11px]`}>
                        Access begins when approved and expires after the approved duration.
                    </span>
                </section>

                {error ? (
                    <p className="m-0 border border-red-400/40 bg-red-400/10 px-[10px] py-[8px] text-[11px] leading-[1.4] text-red-400">
                        {error}
                    </p>
                ) : null}

                <div className="flex items-center justify-end gap-[8px]">
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-[30px] border border-[color:var(--line)] bg-transparent px-[12px] text-[11px] uppercase tracking-[0.08em] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy || !targetReady || !justificationReady}
                        className="h-[30px] border border-[color:var(--green)] bg-[color:var(--green-soft)] px-[12px] text-[11px] font-medium uppercase tracking-[0.04em] text-[color:var(--ink)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                    >
                        {busy ? "Sending…" : `Request ${formatWindow(seconds)}`}
                    </button>
                </div>
            </form>
        </OverlayDialog>
    );
}
